import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';

const SAMPLE_WEBHOOK_PAYLOAD = `
\`\`\`json
{
  "event": "transcriptionComplete",
  "file": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "folderId": "987fde65-4321-abcd-ef98-765432198000",
    "summary": "[00:00:00] Meeting begins with introductions\\n[00:01:30] Discussion of Q4 goals\\n[00:15:45] Action items review\\n[00:30:00] Meeting concludes",
    "url": "https://store.screenapp.io/vid/636adab85ea1ce00121690a9/7578091c-1c46-4c45-806e-02ca4351a2f9.mp4",
    "owner": {
      "name": "John Smith",
      "picture": "https://example.com/profiles/john-smith.jpg"
    },
    "userPromptResponses": {}
  }
}
\`\`\`
`;

interface LearnMoreProps {
  open: boolean;
  onClose: () => void;
  note?: React.ReactNode;
}

const LearnMore: React.FC<LearnMoreProps> = ({ open, onClose, note }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>Understanding Webhooks</DialogTitle>
    <DialogContent>
      <DialogContentText component="div">
        <Typography paragraph>
          Webhooks allow you to receive real-time notifications when
          transcription processing is complete. Each webhook delivers a POST
          request containing file details, transcription text, and AI-generated
          summaries.
        </Typography>

        {note && (
          <Typography paragraph>
            <strong>Note:</strong> {note}
          </Typography>
        )}

        <Typography variant="h6" gutterBottom>
          Sample Webhook Payload
        </Typography>

        <Box
          sx={{
            bgcolor: 'grey.100',
            p: 2,
            borderRadius: 1,
            mb: 2,
            overflowX: 'auto',
          }}
        >
          <ReactMarkdown>{SAMPLE_WEBHOOK_PAYLOAD}</ReactMarkdown>
        </Box>

        <Typography variant="h6" gutterBottom>
          Common Integration Use Cases
        </Typography>

        <Typography variant="subtitle1" gutterBottom>
          Content Management
        </Typography>
        <ul>
          <li>Auto-publish transcripts to your CMS</li>
          <li>Update documentation in Notion or Confluence</li>
          <li>Archive meeting summaries in your knowledge base</li>
        </ul>

        <Typography variant="subtitle1" gutterBottom>
          Team Collaboration
        </Typography>
        <ul>
          <li>Create tasks from meeting action items</li>
          <li>Share summaries in Slack or Microsoft Teams</li>
          <li>Update project status in task management tools</li>
        </ul>

        <Typography variant="subtitle1" gutterBottom>
          Custom Processing
        </Typography>
        <ul>
          <li>Trigger custom workflows with transcription data</li>
          <li>Feed content into your analytics pipeline</li>
          <li>Generate custom reports or notifications</li>
        </ul>
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant="contained">
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

export default LearnMore;
